
import React from "react";
import type { RedirectStats as RedirectStatsType } from "@/types/redirectTypes";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  FilesIcon,
  CheckCircle2Icon,
  XCircleIcon,
  ArrowRightIcon,
  LineChartIcon,
} from "lucide-react";

interface RedirectStatsProps {
  stats: RedirectStatsType | null;
  isLoading: boolean;
}

const RedirectStats: React.FC<RedirectStatsProps> = ({ stats, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-12 bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No stats available</CardTitle>
          <CardDescription>
            Create your first redirect to see statistics
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Total Redirects
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold flex items-center">
            <FilesIcon className="mr-2 h-5 w-5 text-muted-foreground" />
            {stats.total}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Active Redirects
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold flex items-center">
            <CheckCircle2Icon className="mr-2 h-5 w-5 text-green-500" />
            {stats.active}
            <span className="text-sm text-muted-foreground ml-2">
              ({Math.round((stats.active / stats.total) * 100) || 0}%)
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Inactive Redirects
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold flex items-center">
            <XCircleIcon className="mr-2 h-5 w-5 text-gray-400" />
            {stats.inactive}
            <span className="text-sm text-muted-foreground ml-2">
              ({Math.round((stats.inactive / stats.total) * 100) || 0}%)
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Total Hits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold flex items-center">
            <LineChartIcon className="mr-2 h-5 w-5 text-blue-500" />
            {stats.totalAccesses.toLocaleString()}
          </div>
        </CardContent>
      </Card>

      {stats.topRedirects.length > 0 && (
        <Card className="col-span-1 md:col-span-2 lg:col-span-4">
          <CardHeader>
            <CardTitle>Top Redirects</CardTitle>
            <CardDescription>
              Most frequently accessed redirects
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.topRedirects.map((redirect, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div className="flex items-center space-x-2">
                    <div className="font-medium">
                      {redirect.source_path}
                    </div>
                    <ArrowRightIcon className="h-4 w-4 text-muted-foreground" />
                    <div className="text-muted-foreground">
                      {redirect.target_path}
                    </div>
                  </div>
                  <div className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">
                    {redirect.access_count} hits
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
          {stats.topRedirects.length === 0 && (
            <CardFooter>
              <p className="text-sm text-muted-foreground">
                No redirects have been accessed yet
              </p>
            </CardFooter>
          )}
        </Card>
      )}
    </div>
  );
};

export default RedirectStats;
